"use client"

import { Button } from "@abraxas/ui/ui"
import React from "react"
import { formatEther } from "viem"
import { ViewProduct } from "./ViewProduct"
import { useOwnsNFT } from "./hooks/useOwnsNFT"
import { useListing } from "./hooks/useListing"
import { type WithListing } from "./types"
import type {
  ICLockProduct,
  ILlPhyProduct,
  IProduct,
  ProductType,
} from "@repo/loomlock/model-types"

interface NFTCardProps {
  nft: WithListing<IProduct>
  type: ProductType
  className?: string
  onList?: (nft: IProduct, type: ProductType) => void
}

export const NFTCard: React.FC<NFTCardProps> = ({
  nft,
  type,
  className = "",
  onList,
}) => {
  const { isOwner } = useOwnsNFT({
    tokenId: nft.tokenId,
    productType: nft.productType,
  })


  const { listings } = useListing(nft.productType, nft.tokenId, "pricePerToken")

  const isListed = listings.active.length > 0
  // TODO: support expired
  const isExpired =
    listings.active.length > 0
      ? Number(listings.active[0]?.endTimestamp) * 1000 < Date.now()
      : false

  const getNFTImage = () => {
    if (type === "cLock") {
      return (
        (nft as ICLockProduct).metadata.image || "/images/placeholder-clock.png"
      )
    }
    return (
      (nft as ILlPhyProduct).metadata.image ||
      "/images/placeholder-phygital.png"
    )
  }

  const getNFTName = () => {
    if (type === "cLock") {
      return (nft as ICLockProduct).metadata.name || `cLock #${nft.tokenId}`
    }
    return (nft as ILlPhyProduct).metadata.name || `Phygital #${nft.tokenId}`
  }

  const getNFTDescription = () => {
    if (type === "cLock") {
      return (nft as ICLockProduct).metadata.description || "A unique cLock NFT"
    }
    return (
      (nft as ILlPhyProduct).metadata.description || "A unique Phygital NFT"
    )
  }


  const getPriceDisplay = () => {
    if (!listings[0]) {
      return null
    }

    const priceInEth = formatEther(listings[0].pricePerToken)
    return `${parseFloat(priceInEth).toFixed(4)} ETH`
  }

  const getBadgeText = () => {
    if (type === "cLock") {
      return "cLock"
    }
    return "Phygital"
  }

  return (
    <div
      className={`relative overflow-hidden text-brand-blue-500 rounded-lg border border-neutral-grey-200 bg-white shadow-md hover:shadow-lg transition-shadow ${className}`}
    >
      <div className="relative h-64 bg-linear-to-br from-purple-800 to-black text-brand-blue-500">
        <img
          src={getNFTImage()}
          alt={getNFTName()}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "/images/placeholder-nft.png"
          }}
        />

        {/* Type Badge */}
        <div className="absolute top-2 left-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${type === "cLock" ? "bg-blue-500 text-white" : "bg-green-500 text-white"}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${type === "cLock" ? "bg-blue-300" : "bg-green-300"}`}
            />
            {getBadgeText()}
          </span>
        </div>

        {/* Listing Overlay */}
        {isListed && !isExpired && (
          <div className="absolute top-2 right-2">
            <div className="bg-yellow-500 text-black px-2 py-1 rounded-md text-sm font-semibold">
              {getPriceDisplay()}
            </div>
          </div>
        )}

        {/* Expired Badge */}
        {isListed && isExpired && (
          <div className="absolute top-2 right-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-red-500 text-white">
              <span className="w-2 h-2 rounded-full bg-red-300" />
              Expired
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold truncate">{getNFTName()}</h3>
        </div>

        <div className="space-y-2">
          <p className="text-gray-400 text-sm line-clamp-2">
            {getNFTDescription()}
          </p>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Token ID: {nft.tokenId}</span>
          </div>

          {isListed && !isExpired && (
            <div className="pt-2 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-green-400 font-semibold">
                  {getPriceDisplay()}
                </span>
                <span className="text-xs text-gray-500">
                  {listings.totalAvailableQuantity} available
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-neutral-grey-200 p-3 flex gap-2">
        {/* Owner who hasn't listed - can list */}
        {isOwner && !isListed && (
          <Button
            size="small"
            onClick={() => onList?.(nft, type)}
            className="flex-1"
          >
            List
          </Button>
        )}

        <ViewProduct product={nft} />

        {/* Owner when listed - can list more or edit listing */}
        {isOwner && isListed && !isExpired && (
          <div className="flex gap-1 flex-1">
            <Button
              size="small"
              onClick={() => onList?.(nft, type)}
              className="flex-1 text-xs"
            >
              Edit Listing
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
